import { BaseService, PagedResult, QueryParams } from './base/base.service';

export type ReferralStatus = 'Pending' | 'Active' | 'Expired' | 'Cancelled';

export interface AdminReferral extends Record<string, unknown> {
  id: string;
  referrerId: string;
  referrerName: string;
  referrerEmail: string;
  referredId: string;
  referredName: string;
  referredEmail: string;
  referralCode?: string;
  status: ReferralStatus;
  qualifiedAt?: string;
  expiresAt?: string;
  creationTime?: string;
  lastModificationTime?: string;
  referrerCoinsEarned: number;
  referrerTSparksEarned: number;
  referredCoinsEarned: number;
  referredTSparksEarned: number;
  fraudScore: number;
  fraudFlags?: string[];
  ipAddress?: string;
  deviceId?: string;
  userAgent?: string;
  cancelledReason?: string;
  adminNotes?: string;
}

export interface TopReferrer {
  userId: string;
  userName: string;
  successfulReferrals: number;
  totalRewards: number;
}

export interface FraudTrendPoint {
  date: string;
  totalReferrals: number;
  suspiciousReferrals: number;
  averageFraudScore: number;
  topFlags?: string[];
}

export interface ReferralPlatformStats {
  totalReferrals: number;
  pendingReferrals: number;
  activeReferrals: number;
  expiredReferrals: number;
  cancelledReferrals: number;
  conversionRate?: number;
  fraudRatio?: number;
  averageFraudScore?: number;
  totalRewardsIssued?: number;
  totalCoinsIssued?: number;
  totalTSparksIssued?: number;
  highFraudReferrals?: number;
  autoCancelledReferrals?: number;
  fraudFlagCounts?: Record<string, number>;
  topReferrers?: TopReferrer[];
  fraudTrends?: FraudTrendPoint[];
}

export interface ReferralQueryParams extends QueryParams {
  search?: string;
  status?: ReferralStatus;
  minFraudScore?: number;
  maxFraudScore?: number;
  referrerId?: string;
  referredId?: string;
  campaignId?: string;
  flaggedOnly?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface UpdateReferralStatusInput {
  status: ReferralStatus;
  cancelledReason?: string;
  adminNotes?: string;
  fraudScore?: number;
  fraudFlags?: string[];
}

class ReferralService extends BaseService {
  constructor() {
    super('/api/app/referral');
  }

  async getPlatformStats(): Promise<ReferralPlatformStats> {
    return this.get<ReferralPlatformStats>('/platform-stats');
  }

  async getReferrals(params: ReferralQueryParams = {}): Promise<PagedResult<AdminReferral>> {
    const query = new URLSearchParams();

    if (params.status) query.append('Status', params.status);
    if (params.search) query.append('Search', params.search);
    if (params.minFraudScore !== undefined) {
      query.append('MinFraudScore', params.minFraudScore.toString());
    }
    if (params.maxFraudScore !== undefined) {
      query.append('MaxFraudScore', params.maxFraudScore.toString());
    }
    if (params.referrerId) query.append('ReferrerId', params.referrerId);
    if (params.referredId) query.append('ReferredId', params.referredId);
    if (params.campaignId) query.append('CampaignId', params.campaignId);
    if (params.flaggedOnly) query.append('FlaggedOnly', 'true');
    if (params.startDate) query.append('StartDate', params.startDate);
    if (params.endDate) query.append('EndDate', params.endDate);
    if (params.skipCount !== undefined) {
      query.append('SkipCount', params.skipCount.toString());
    }
    if (params.maxResultCount !== undefined) {
      query.append('MaxResultCount', params.maxResultCount.toString());
    }
    if (params.sorting) query.append('Sorting', params.sorting);

    const qs = query.toString();
    const url = qs ? `/all?${qs}` : '/all';
    return this.get<PagedResult<AdminReferral>>(url);
  }

  async updateReferralStatus(
    id: string,
    payload: UpdateReferralStatusInput,
  ): Promise<AdminReferral> {
    return this.put<AdminReferral, UpdateReferralStatusInput>(
      `/${id}/status`,
      payload,
    );
  }
}

export const referralService = new ReferralService();
